import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  ChartLine, CalendarBlank, EnvelopeSimple, CreditCard, Users, Stack,
  GraduationCap, ArrowsClockwise, Trash, Plus, CheckCircle, Circle, Star,
  ShieldCheck, UserList, ClockCounterClockwise, ArrowSquareOut, Hourglass, Printer
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import WaitlistAdmin from "@/components/admin/WaitlistAdmin";
import { AddStudentModal, AssignDateModal, RecordPaymentModal, StatusSelect, PaymentCell, STATUS_LABELS } from "@/components/admin/EnrollmentTools";

const TABS = [
  { id: "overview", label: "Overview", Icon: ChartLine },
  { id: "signups", label: "Signups", Icon: UserList },
  { id: "bookings", label: "Bookings", Icon: CalendarBlank },
  { id: "messages", label: "Messages", Icon: EnvelopeSimple },
  { id: "transactions", label: "Transactions", Icon: CreditCard },
  { id: "students", label: "Students", Icon: Users },
  { id: "sessions", label: "Class Sessions", Icon: Stack },
  { id: "waitlist", label: "Waitlist", Icon: Hourglass },
  { id: "courses", label: "Courses", Icon: GraduationCap },
  { id: "elearning", label: "eLearning", Icon: ShieldCheck },
  { id: "audit", label: "Audit Log", Icon: ClockCounterClockwise },
];

const STATUS_OPTIONS = ["pending", "contacted", "quoted", "converted", "closed"];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";
const fmtDay = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "—";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [data, setData] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!loading && (!user || (user.role !== "admin" && user.role !== "instructor"))) navigate("/login?next=/admin");
    if (!loading && user?.must_change_password) navigate("/change-password");
  }, [user, loading, navigate]);

  const isInstructorOnly = user?.role === "instructor";
  const visibleTabs = isInstructorOnly ? TABS.filter((t) => t.id === "signups") : TABS;

  useEffect(() => {
    if (isInstructorOnly && tab !== "signups") setTab("signups");
  }, [isInstructorOnly, tab]);

  useEffect(() => {
    if (!user) return;
    const endpoints = {
      overview: "/admin/overview",
      signups: "/admin/enrollments",
      bookings: "/admin/bookings",
      messages: "/admin/contacts",
      transactions: "/admin/transactions",
      students: "/admin/users",
      sessions: "/admin/sessions",
      waitlist: "/admin/waitlist",
      courses: "/courses",
      elearning: "/admin/elearning/analytics",
      audit: "/admin/audit-logs",
    };
    if (!endpoints[tab]) return;
    if (isInstructorOnly && tab !== "signups") return;
    api.get(endpoints[tab]).then(({ data: d }) => setData((p) => ({ ...p, [tab]: d }))).catch(() => {});
  }, [tab, user, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  if (loading || !user) return <div className="container-x py-32 font-mono text-sm">Loading...</div>;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <Seo title="Admin Portal" path="/admin" noindex />
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="container-x py-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-2 text-[#FFD300]">// ADMIN PORTAL · {user.email.toUpperCase()}</div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tighter">Operations Console</h1>
          </div>
          <button onClick={refresh} data-testid="admin-refresh" className="btn-ghost"><ArrowsClockwise size={14} /> Refresh</button>
        </div>
      </div>

      <div className="container-x py-8 grid grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-3">
          <nav className="surface-card p-3 sticky top-24" data-testid="admin-nav">
            {TABS.map((t) => {
              if (!visibleTabs.find((v) => v.id === t.id)) return null;
              const Icon = t.Icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); window.history.replaceState(null, "", `/admin?tab=${t.id}`); }}
                  data-testid={`tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left font-mono text-xs tracking-[0.12em] uppercase transition-colors ${active ? "bg-[#FFD300] text-black" : "text-neutral-300 hover:bg-white/5"}`}
                >
                  <Icon size={16} weight={active ? "fill" : "duotone"} /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="col-span-12 lg:col-span-9">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {tab === "overview" && <Overview d={data.overview} />}
            {tab === "signups" && <Signups d={data.signups || []} onUpdate={refresh} userRole={user.role} />}
            {tab === "bookings" && <Bookings d={data.bookings || []} onUpdate={refresh} />}
            {tab === "messages" && <Messages d={data.messages || []} onUpdate={refresh} />}
            {tab === "transactions" && <Transactions d={data.transactions || []} />}
            {tab === "students" && <Students d={data.students || []} onUpdate={refresh} />}
            {tab === "sessions" && <Sessions d={data.sessions || []} courses={data.courses || []} onUpdate={refresh} loadCourses={() => api.get("/courses").then(({ data: c }) => setData((p) => ({ ...p, courses: c })))} />}
            {tab === "waitlist" && <WaitlistAdmin d={data.waitlist || []} onUpdate={refresh} />}
            {tab === "courses" && <Courses d={data.courses || []} onUpdate={refresh} />}
            {tab === "elearning" && <ElearningAnalytics d={data.elearning} />}
            {tab === "audit" && <AuditLog d={data.audit || []} />}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

/* ---------- OVERVIEW ---------- */
function Overview({ d }) {
  if (!d) return <Loading />;
  const stats = [
    { label: "Revenue (all-time)", value: money(d.revenue_total), Icon: CreditCard },
    { label: "Revenue (this month)", value: money(d.revenue_month), Icon: ChartLine },
    { label: "Total students", value: d.total_students, sub: `+${d.new_students_week} this week`, Icon: Users },
    { label: "Pending bookings", value: d.pending_bookings, Icon: CalendarBlank, alert: d.pending_bookings > 0 },
    { label: "Unread messages", value: d.unread_contacts, Icon: EnvelopeSimple, alert: d.unread_contacts > 0 },
    { label: "Upcoming sessions", value: d.upcoming_sessions, Icon: Stack },
    { label: "Total enrollments", value: d.total_enrollments, Icon: GraduationCap },
  ];
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
        {stats.map((s) => (
          <div key={s.label} className={`bg-black p-5 ${s.alert ? "ring-1 ring-[#FFD300]/40" : ""}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <s.Icon size={20} weight="duotone" className={`mb-3 ${s.alert ? "text-[#FFD300]" : "text-neutral-400"}`} />
            <div className="font-heading text-2xl tracking-tighter">{s.value}</div>
            <div className="overline mt-1">{s.label}</div>
            {s.sub && <div className="font-mono text-[10px] text-[#FFD300] mt-1">{s.sub}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="surface-card p-6" data-testid="recent-bookings">
          <div className="overline mb-3">// LATEST 5 BOOKINGS</div>
          {(d.recent_bookings || []).length === 0 && <div className="text-neutral-500 text-sm">No bookings yet.</div>}
          <ul className="space-y-3">
            {(d.recent_bookings || []).map((b) => (
              <li key={b.id} className="border-b border-white/5 pb-3 text-sm">
                <div className="flex justify-between"><span className="text-white font-medium">{b.name}</span><span className="font-mono text-xs text-neutral-500">{fmtDay(b.created_at)}</span></div>
                <div className="text-neutral-400 text-xs mt-1">{b.service_type} · {b.location}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="surface-card p-6" data-testid="recent-messages">
          <div className="overline mb-3">// LATEST 5 MESSAGES</div>
          {(d.recent_contacts || []).length === 0 && <div className="text-neutral-500 text-sm">No messages yet.</div>}
          <ul className="space-y-3">
            {(d.recent_contacts || []).map((c) => (
              <li key={c.id} className="border-b border-white/5 pb-3 text-sm">
                <div className="flex justify-between"><span className="text-white font-medium">{c.name}</span><span className="font-mono text-xs text-neutral-500">{fmtDay(c.created_at)}</span></div>
                <div className="text-neutral-400 text-xs mt-1 line-clamp-1">{c.subject}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ---------- BOOKINGS ---------- */
function Bookings({ d, onUpdate }) {
  const setStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    onUpdate();
  };
  return (
    <div className="space-y-4" data-testid="admin-bookings">
      <div className="flex justify-between items-end">
        <h2 className="font-heading text-3xl tracking-tighter">Bookings <span className="text-neutral-500 text-base">({d.length})</span></h2>
      </div>
      {d.length === 0 ? <Empty msg="No booking requests yet." /> : (
        <div className="space-y-2">
          {d.map((b) => (
            <div key={b.id} className="surface-card p-5" data-testid={`booking-${b.id}`}>
              <div className="grid grid-cols-12 gap-4 items-start">
                <div className="col-span-12 md:col-span-3">
                  <div className="font-heading text-lg">{b.name}</div>
                  <div className="font-mono text-xs text-neutral-400 mt-1">{b.email}</div>
                  <div className="font-mono text-xs text-neutral-400">{b.phone}</div>
                </div>
                <div className="col-span-12 md:col-span-5">
                  <div className="overline mb-1">{b.service_type}</div>
                  <div className="text-sm text-neutral-300">{b.location} · {b.preferred_date}</div>
                  {b.details && <div className="text-xs text-neutral-500 mt-2 line-clamp-3">{b.details}</div>}
                </div>
                <div className="col-span-12 md:col-span-4 flex flex-col gap-2">
                  <div className="font-mono text-[10px] text-neutral-500 uppercase">{fmtDate(b.created_at)}</div>
                  <select
                    value={b.status || "pending"}
                    onChange={(e) => setStatus(b.id, e.target.value)}
                    data-testid={`booking-status-${b.id}`}
                    className="field text-xs py-2"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                  <a href={`mailto:${b.email}?subject=Re: ${b.service_type} booking request`} className="btn-ghost text-[10px]">
                    Email reply →
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- MESSAGES ---------- */
function Messages({ d, onUpdate }) {
  const toggleRead = async (c) => {
    await api.patch(`/admin/contacts/${c.id}`, { read: !c.read });
    onUpdate();
  };
  return (
    <div className="space-y-4" data-testid="admin-messages">
      <h2 className="font-heading text-3xl tracking-tighter">Messages <span className="text-neutral-500 text-base">({d.length})</span></h2>
      {d.length === 0 ? <Empty msg="No contact messages yet." /> : (
        <div className="space-y-2">
          {d.map((c) => (
            <div key={c.id} className={`surface-card p-5 ${c.read ? "opacity-60" : "ring-1 ring-[#FFD300]/30"}`} data-testid={`message-${c.id}`}>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-3">
                  <button onClick={() => toggleRead(c)} className="mb-2" data-testid={`mark-read-${c.id}`}>
                    {c.read ? <CheckCircle size={18} weight="duotone" className="text-[#039855]" /> : <Circle size={18} className="text-[#FFD300]" />}
                  </button>
                  <div className="font-heading text-lg">{c.name}</div>
                  <div className="font-mono text-xs text-neutral-400">{c.email}</div>
                  {c.phone && <div className="font-mono text-xs text-neutral-400">{c.phone}</div>}
                </div>
                <div className="col-span-12 md:col-span-7">
                  <div className="overline mb-2">{c.subject}</div>
                  <div className="text-sm text-neutral-200 whitespace-pre-line">{c.message}</div>
                </div>
                <div className="col-span-12 md:col-span-2 text-right">
                  <div className="font-mono text-[10px] text-neutral-500 uppercase">{fmtDate(c.created_at)}</div>
                  <a href={`mailto:${c.email}?subject=Re: ${c.subject}`} className="btn-ghost text-[10px] mt-3 inline-flex">Reply →</a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- SIGNUPS (per-student enrollment list with contact + cohort) ---------- */
function Signups({ d, onUpdate, userRole }) {
  const [query, setQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [busyId, setBusyId] = useState("");
  const [modal, setModal] = useState(null); // {type:"add"} | {type:"assign", e} | {type:"pay", e}

  const courseOptions = Array.from(new Set(d.map((e) => e.course?.slug).filter(Boolean))).sort();
  const q = query.trim().toLowerCase();
  const filtered = d.filter((e) => {
    if (courseFilter !== "ALL" && e.course?.slug !== courseFilter) return false;
    if (statusFilter === "NO_DATE") { if (e.class_session) return false; }
    else if (statusFilter === "BALANCE_DUE") { if (!((e.balance_due_cents || 0) > 0 && !e.balance_paid)) return false; }
    else if (statusFilter !== "ALL" && (e.status || "scheduled") !== statusFilter) return false;
    if (!q) return true;
    return (
      (e.student?.name || "").toLowerCase().includes(q) ||
      (e.student?.email || "").toLowerCase().includes(q) ||
      (e.student?.phone || "").toLowerCase().includes(q) ||
      (e.course?.title || "").toLowerCase().includes(q)
    );
  });

  const reopen = async (e) => {
    if (!window.confirm("Reopen this enrollment? (Certificate will remain on file.)")) return;
    setBusyId(e.id);
    try {
      await api.post(`/admin/enrollments/${e.id}/reopen`);
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reopen.");
    } finally {
      setBusyId("");
    }
  };

  const generateCert = async (e) => {
    if (!window.confirm(`Auto-generate a PDF certificate for ${e.student?.name || e.student?.email}?`)) return;
    setBusyId(e.id);
    try {
      await api.post(`/admin/enrollments/${e.id}/certificate/generate`);
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate certificate.");
    } finally {
      setBusyId("");
    }
  };

  const uploadCert = async (e, file) => {
    if (!file) return;
    setBusyId(e.id);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/enrollments/${e.id}/certificate`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed.");
    } finally {
      setBusyId("");
    }
  };

  const downloadCert = async (e) => {
    try {
      const res = await api.get(`/enrollments/${e.id}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEP-Certificate-${e.certificate_id || e.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to download certificate.");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-signups">
      {modal?.type === "add" && <AddStudentModal onClose={() => setModal(null)} onDone={onUpdate} />}
      {modal?.type === "assign" && <AssignDateModal enrollment={modal.e} onClose={() => setModal(null)} onDone={onUpdate} />}
      {modal?.type === "pay" && <RecordPaymentModal enrollment={modal.e} onClose={() => setModal(null)} onDone={onUpdate} />}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h2 className="font-heading text-3xl tracking-tighter">
          Course Signups <span className="text-neutral-500 text-base">({filtered.length}/{d.length})</span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setModal({ type: "add" })} className="font-mono text-xs font-bold tracking-[0.14em] uppercase bg-[#FFD300] text-black px-4 py-2 hover:bg-[#FFE149]" data-testid="add-student-btn">
            + Add student to course
          </button>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, course…"
            className="bg-black border border-white/10 px-3 py-2 text-sm font-mono w-64"
            data-testid="signup-search"
          />
          <select
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
            className="bg-black border border-white/10 px-3 py-2 text-sm font-mono"
            data-testid="signup-course-filter"
          >
            <option value="ALL">All courses</option>
            {courseOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-black border border-white/10 px-3 py-2 text-sm font-mono"
            data-testid="signup-status-filter"
          >
            <option value="ALL">All status</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            <option value="NO_DATE">No class date</option>
            <option value="BALANCE_DUE">Balance due</option>
          </select>
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        {filtered.length === 0 ? <div className="p-8"><Empty msg="No signups match these filters." /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr>
                <th className="p-3 overline">Enrolled</th>
                <th className="p-3 overline">Student</th>
                <th className="p-3 overline">Course</th>
                <th className="p-3 overline">Cohort & Payment</th>
                <th className="p-3 overline">Status</th>
                <th className="p-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const status = e.status || "scheduled";
                const isCompleted = status === "completed";
                const busy = busyId === e.id;
                return (
                <tr key={e.id} className="border-t border-white/5 align-top hover:bg-white/[0.02]" data-testid={`signup-${e.id}`}>
                  <td className="p-3 font-mono text-xs text-neutral-400 whitespace-nowrap">{fmtDate(e.enrolled_at)}</td>
                  <td className="p-3">
                    <div className="font-medium text-white">{e.student?.name || "—"}</div>
                    <div className="text-xs text-neutral-400">
                      <a href={`mailto:${e.student?.email || ""}`} className="hover:text-[#FFD300]">{e.student?.email}</a>
                    </div>
                    {e.student?.phone && (
                      <div className="text-xs text-neutral-500 font-mono">
                        <a href={`tel:${e.student.phone}`} className="hover:text-[#FFD300]">{e.student.phone}</a>
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-white">{e.course?.title || e.course_id}</div>
                    <div className="text-xs text-neutral-500 font-mono">
                      {e.course?.code}{e.course?.state ? ` · ${e.course.state}` : ""} · {money(e.course?.price)}
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {e.class_session ? (
                      <>
                        <div className="text-white">{fmtDay(e.class_session.starts_at)}</div>
                        <div className="text-neutral-500 font-mono">{e.class_session.instructor || ""}</div>
                        <div className="text-neutral-500">{(e.class_session.venue || "").split(" — ")[0]}</div>
                      </>
                    ) : <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-orange-300" data-testid={`no-date-${e.id}`}>No date picked</span>}
                    {!isCompleted && status !== "cancelled" && (
                      <button onClick={() => setModal({ type: "assign", e })} className="mt-1.5 block text-[10px] font-mono tracking-[0.14em] uppercase text-[#FFD300] hover:underline" data-testid={`assign-date-${e.id}`}>
                        {e.class_session ? "Move date" : "Assign date"}
                      </button>
                    )}
                    <div className="mt-2"><PaymentCell enrollment={e} /></div>
                    {(e.balance_due_cents || 0) > 0 && !e.balance_paid && status !== "cancelled" && (
                      <button onClick={() => setModal({ type: "pay", e })} className="mt-1 block text-[10px] font-mono tracking-[0.14em] uppercase text-emerald-300 hover:underline" data-testid={`record-payment-${e.id}`}>
                        Paid in person →
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    <StatusSelect enrollment={e} onDone={onUpdate} />
                    {e.status_note && <div className="text-[10px] text-neutral-500 mt-1 max-w-[140px]" data-testid={`status-note-${e.id}`}>{e.status_note}</div>}
                    {e.has_certificate && (
                      <div className="text-[10px] font-mono text-[#FFD300] mt-1">✓ Cert on file</div>
                    )}
                    {e.certificate_id && (
                      <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{e.certificate_id}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    <div className="flex flex-col gap-1.5 min-w-[160px]">
                      {isCompleted && (
                        <button
                          disabled={busy}
                          onClick={() => reopen(e)}
                          className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/10 text-neutral-300 font-mono text-[10px] tracking-[0.14em] uppercase disabled:opacity-50"
                          data-testid={`reopen-${e.id}`}
                        >
                          Reopen
                        </button>
                      )}
                      <button
                        disabled={busy}
                        onClick={() => generateCert(e)}
                        className="w-full px-2 py-1.5 bg-[#FFD300] hover:bg-[#FFE149] text-black font-mono text-[10px] tracking-[0.14em] uppercase disabled:opacity-50"
                        data-testid={`gen-cert-${e.id}`}
                      >
                        {e.has_certificate ? "Regenerate PDF" : "Auto-Generate"}
                      </button>
                      <label className={`w-full px-2 py-1.5 border border-white/15 hover:bg-white/5 text-neutral-200 font-mono text-[10px] tracking-[0.14em] uppercase text-center cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                        Upload Cert
                        <input
                          type="file"
                          accept="application/pdf,image/png,image/jpeg"
                          className="hidden"
                          onChange={(ev) => uploadCert(e, ev.target.files?.[0])}
                          data-testid={`upload-cert-${e.id}`}
                        />
                      </label>
                      {e.has_certificate && (
                        <button
                          onClick={() => downloadCert(e)}
                          className="w-full px-2 py-1.5 border border-[#FFD300]/40 text-[#FFD300] hover:bg-[#FFD300]/10 font-mono text-[10px] tracking-[0.14em] uppercase"
                          data-testid={`dl-cert-${e.id}`}
                        >
                          Download
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}



/* ---------- TRANSACTIONS ---------- */
/* ---------- AUDIT LOG ---------- */
function AuditLog({ d }) {
  return (
    <div className="space-y-4" data-testid="admin-audit">
      <h2 className="font-heading text-3xl tracking-tighter">Audit Log <span className="text-neutral-500 text-base">(last {d.length})</span></h2>
      <p className="text-neutral-400 text-sm">Every sign-in, password change and admin action — who, what, when, from where.</p>
      <div className="surface-card overflow-x-auto">
        {d.length === 0 ? <div className="p-8"><Empty msg="No audit events yet." /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr><th className="p-3 overline">When</th><th className="p-3 overline">Actor</th><th className="p-3 overline">Action</th><th className="p-3 overline">Target</th><th className="p-3 overline">IP</th></tr>
            </thead>
            <tbody>
              {d.map((a, i) => (
                <tr key={`${a.ts}-${i}`} className="border-t border-white/5" data-testid={`audit-row-${i}`}>
                  <td className="p-3 font-mono text-xs whitespace-nowrap">{fmtDate(a.ts)}</td>
                  <td className="p-3 text-xs">{a.actor_email || "—"}{a.actor_role && <span className="ml-2 code-badge">{a.actor_role}</span>}</td>
                  <td className="p-3"><span className={`code-badge ${a.action.includes("failed") ? "text-red-400 border-red-400/40" : ""}`}>{a.action}</span></td>
                  <td className="p-3 font-mono text-[11px] text-neutral-400 max-w-[220px] truncate">{a.target || (a.meta && Object.keys(a.meta).length ? JSON.stringify(a.meta) : "—")}</td>
                  <td className="p-3 font-mono text-xs text-neutral-400">{a.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Transactions({ d }) {
  return (
    <div className="space-y-4" data-testid="admin-transactions">
      <h2 className="font-heading text-3xl tracking-tighter">Transactions <span className="text-neutral-500 text-base">({d.length})</span></h2>
      <div className="surface-card overflow-hidden">
        {d.length === 0 ? <div className="p-8"><Empty msg="No transactions yet." /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr><th className="p-3 overline">Date</th><th className="p-3 overline">Email</th><th className="p-3 overline">Amount</th><th className="p-3 overline">Status</th><th className="p-3 overline">Processor</th><th className="p-3 overline">Payment ID</th><th className="p-3 overline">Courses</th></tr>
            </thead>
            <tbody>
              {d.map((t) => (
                <tr key={t.session_id} className="border-t border-white/5" data-testid={`txn-${t.session_id}`}>
                  <td className="p-3 font-mono text-xs">{fmtDate(t.created_at)}</td>
                  <td className="p-3">{t.email}</td>
                  <td className="p-3 font-mono">{money(t.amount)}</td>
                  <td className="p-3"><span className={`code-badge ${t.payment_status === "paid" ? "" : "opacity-50"}`}>{t.payment_status}</span></td>
                  <td className="p-3"><span className={`code-badge ${t.provider === "square" ? "text-[#FFD300] border-[#FFD300]/40" : t.provider === "klarna" ? "text-pink-300 border-pink-300/40" : "opacity-60"}`} data-testid={`txn-provider-${t.session_id}`}>{t.provider}</span></td>
                  <td className="p-3 font-mono text-[11px] text-neutral-400">
                    <span className="inline-block max-w-[160px] truncate align-middle" title={t.provider_ref}>{t.provider_ref || "—"}</span>
                    {t.provider_url && (
                      <a href={t.provider_url} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex items-center gap-1 text-[#FFD300] hover:underline align-middle" data-testid={`txn-open-${t.session_id}`}>
                        Open in {t.provider === "square" ? "Square" : t.provider === "klarna" ? "Klarna" : "Stripe"} <ArrowSquareOut size={11} />
                      </a>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-neutral-400">{(t.course_ids || []).length} course(s)</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------- STUDENTS ---------- */
function Students({ d, onUpdate }) {
  const changeRole = async (u, next) => {
    if (!window.confirm(`Change ${u.email} to role: ${next}?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/role`, { role: next });
      onUpdate?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to change role.");
    }
  };
  return (
    <div className="space-y-4" data-testid="admin-students">
      <h2 className="font-heading text-3xl tracking-tighter">Students & Users <span className="text-neutral-500 text-base">({d.length})</span></h2>
      <div className="surface-card overflow-hidden">
        {d.length === 0 ? <div className="p-8"><Empty msg="No users yet." /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr><th className="p-3 overline">Name</th><th className="p-3 overline">Email</th><th className="p-3 overline">Role</th><th className="p-3 overline">Phone</th><th className="p-3 overline">Enrollments</th><th className="p-3 overline">Joined</th><th className="p-3 overline">Actions</th></tr>
            </thead>
            <tbody>
              {d.map((u) => (
                <tr key={u.id} className="border-t border-white/5" data-testid={`user-${u.id}`}>
                  <td className="p-3">{u.name}</td>
                  <td className="p-3 font-mono text-xs">{u.email}</td>
                  <td className="p-3"><span className="state-tag">{u.role}</span></td>
                  <td className="p-3 font-mono text-xs">{u.phone || "—"}</td>
                  <td className="p-3 font-mono">{u.enrollment_count || 0}</td>
                  <td className="p-3 font-mono text-xs text-neutral-400">{fmtDay(u.created_at)}</td>
                  <td className="p-3">
                    <select
                      value={u.role}
                      onChange={(ev) => changeRole(u, ev.target.value)}
                      className="bg-black border border-white/10 px-2 py-1 text-xs font-mono"
                      data-testid={`role-select-${u.id}`}
                    >
                      <option value="student">student</option>
                      <option value="instructor">instructor</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------- CLASS SESSIONS ---------- */
function Sessions({ d, courses, onUpdate, loadCourses }) {
  const [form, setForm] = useState({ course_id: "", starts_at: "", venue: "SEP HQ Range — Baltimore, MD", instructor: "S. Williams", capacity: 8 });
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (courses.length === 0) loadCourses(); }, [courses.length, loadCourses]);

  const create = async () => {
    setCreating(true);
    try {
      const isoStart = new Date(form.starts_at).toISOString();
      await api.post("/admin/sessions", { ...form, starts_at: isoStart });
      setForm({ ...form, starts_at: "" });
      onUpdate();
    } catch (e) {
      alert("Failed to create session");
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this class session? Enrolled students will need to be re-scheduled.")) return;
    await api.delete(`/admin/sessions/${id}`);
    onUpdate();
  };

  const future = d.filter((s) => new Date(s.starts_at) >= new Date());
  const [toppingUp, setToppingUp] = useState(false);
  const [topupResult, setTopupResult] = useState(null);
  const runTopup = async () => {
    setToppingUp(true);
    setTopupResult(null);
    try {
      const { data: r } = await api.post("/admin/sessions/topup");
      setTopupResult(r);
      onUpdate();
    } catch (e) {
      setTopupResult({ error: true, message: e?.message || "Top-up failed" });
    } finally {
      setToppingUp(false);
    }
  };
  return (
    <div className="space-y-6" data-testid="admin-sessions">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h2 className="font-heading text-3xl tracking-tighter">Class Sessions <span className="text-neutral-500 text-base">({future.length} upcoming · {d.length} total)</span></h2>
        <button
          onClick={runTopup}
          disabled={toppingUp}
          className="font-mono text-xs font-bold tracking-[0.14em] uppercase bg-[#FFD300] text-black px-4 py-2.5 hover:bg-[#FFE149] disabled:opacity-60 flex items-center gap-2"
          data-testid="topup-sessions-btn"
          title="Ensure every course has at least 6 upcoming class sessions"
        >
          <ArrowsClockwise size={14} weight="bold" className={toppingUp ? "animate-spin" : ""} />
          {toppingUp ? "Topping up…" : "Top-up upcoming sessions"}
        </button>
      </div>
      {topupResult && (
        <div
          data-testid="topup-result"
          className={`surface-card p-4 text-sm font-mono ${topupResult.error ? "text-red-400" : "text-neutral-200"}`}
        >
          {topupResult.error
            ? `Top-up failed: ${topupResult.message}`
            : topupResult.added_total === 0
              ? "All courses already have 6+ upcoming sessions. No action needed."
              : `Added ${topupResult.added_total} new session${topupResult.added_total > 1 ? "s" : ""} across ${Object.keys(topupResult.per_course).length} course${Object.keys(topupResult.per_course).length > 1 ? "s" : ""}.`}
        </div>
      )}

      <div className="surface-card p-5" data-testid="new-session-form">
        <div className="overline mb-3"><Plus size={11} className="inline mr-1" />ADD NEW SESSION</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} className="field md:col-span-2" data-testid="session-course-select">
            <option value="">Select course...</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.title}</option>)}
          </select>
          <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="field" data-testid="session-date" />
          <input placeholder="Venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="field" data-testid="session-venue" />
          <input placeholder="Capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="field" data-testid="session-capacity" />
          <button onClick={create} disabled={!form.course_id || !form.starts_at || creating} className="btn-primary justify-center" data-testid="session-create-btn">
            {creating ? "..." : "Create"}
          </button>
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        {d.length === 0 ? <div className="p-8"><Empty msg="No class sessions scheduled." /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left">
              <tr><th className="p-3 overline">Date</th><th className="p-3 overline">Course</th><th className="p-3 overline">Venue</th><th className="p-3 overline">Enrolled</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {d.map((s) => (
                <tr key={s.id} className="border-t border-white/5" data-testid={`session-${s.id}`}>
                  <td className="p-3 font-mono text-xs">{fmtDate(s.starts_at)}</td>
                  <td className="p-3">{s.course_title}</td>
                  <td className="p-3 text-neutral-400 text-xs">{s.venue}</td>
                  <td className="p-3 font-mono">{s.enrolled_count}/{s.capacity}</td>
                  <td className="p-3 whitespace-nowrap">
                    <a href={`/admin/roster/${s.id}`} target="_blank" rel="noopener noreferrer" title="Printable sign-in sheet" data-testid={`roster-${s.id}`} className="inline-flex items-center gap-1 text-[#FFD300] hover:underline font-mono text-[10px] uppercase tracking-[0.14em] mr-3"><Printer size={13} /> Roster</a>
                    <button onClick={() => del(s.id)} data-testid={`del-${s.id}`} className="text-red-400 hover:text-red-300 align-middle"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------- COURSES ---------- */
function Courses({ d, onUpdate }) {
  const toggleFeatured = async (c) => {
    await api.patch(`/admin/courses/${c.id}`, { is_featured: !c.is_featured });
    onUpdate();
  };
  const updatePrice = async (c, newPrice) => {
    const p = parseFloat(newPrice);
    if (isNaN(p) || p < 0) return;
    await api.patch(`/admin/courses/${c.id}`, { price: p });
    onUpdate();
  };
  return (
    <div className="space-y-4" data-testid="admin-courses">
      <h2 className="font-heading text-3xl tracking-tighter">Courses <span className="text-neutral-500 text-base">({d.length})</span></h2>
      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left">
            <tr><th className="p-3 overline">Code</th><th className="p-3 overline">Title</th><th className="p-3 overline">Price</th><th className="p-3 overline">Students</th><th className="p-3 overline">Featured</th></tr>
          </thead>
          <tbody>
            {d.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`course-${c.id}`}>
                <td className="p-3"><span className="code-badge">{c.code}</span></td>
                <td className="p-3">{c.title}</td>
                <td className="p-3">
                  <input
                    defaultValue={c.price}
                    type="number"
                    step="5"
                    onBlur={(e) => updatePrice(c, e.target.value)}
                    data-testid={`price-${c.id}`}
                    className="field w-24 text-sm py-1.5"
                  />
                </td>
                <td className="p-3 font-mono">{c.students_count || 0}</td>
                <td className="p-3">
                  <button onClick={() => toggleFeatured(c)} data-testid={`toggle-featured-${c.id}`}>
                    <Star size={18} weight={c.is_featured ? "fill" : "regular"} className={c.is_featured ? "text-[#FFD300]" : "text-neutral-500 hover:text-[#FFD300]"} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-neutral-500 font-mono text-sm py-8 text-center">{msg}</div>;
}
function Loading() {
  return <div className="text-neutral-500 font-mono text-sm py-8">Loading...</div>;
}

/* ---------- ELEARNING ANALYTICS ---------- */
function ElearningAnalytics({ d }) {
  if (!d) return <Loading />;
  return (
    <div className="space-y-8" data-testid="admin-elearning">
      <div className="flex justify-between items-end">
        <h2 className="font-heading text-3xl tracking-tighter">eLearning <span className="text-neutral-500 text-base">U.S. LawShield<sup>®</sup></span></h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
        <div className="bg-black p-5">
          <ShieldCheck size={20} weight="duotone" className="mb-3 text-[#FFD300]" />
          <div className="font-heading text-2xl tracking-tighter">{d.total_clicks}</div>
          <div className="overline mt-1">Total clicks</div>
        </div>
        <div className="bg-black p-5">
          <GraduationCap size={20} weight="duotone" className="mb-3 text-neutral-400" />
          <div className="font-heading text-2xl tracking-tighter">{d.courses_offered}</div>
          <div className="overline mt-1">Courses offered</div>
        </div>
        <div className="bg-black p-5">
          <ChartLine size={20} weight="duotone" className="mb-3 text-neutral-400" />
          <div className="font-heading text-2xl tracking-tighter">{d.by_course?.length || 0}</div>
          <div className="overline mt-1">Courses with clicks</div>
        </div>
      </div>

      <div className="surface-card p-6">
        <div className="overline mb-4">// CLICKS BY COURSE</div>
        {(d.by_course || []).length === 0 ? <Empty msg="No clicks tracked yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-500 border-b border-white/10">
                  <th className="py-2">Course</th>
                  <th className="py-2">Category</th>
                  <th className="py-2">State</th>
                  <th className="py-2 text-right">Clicks</th>
                  <th className="py-2">Last click</th>
                </tr>
              </thead>
              <tbody>
                {d.by_course.map((c) => (
                  <tr key={c.slug} className="border-b border-white/5" data-testid={`elearning-row-${c.slug}`}>
                    <td className="py-3 pr-3 text-white">{c.title}</td>
                    <td className="py-3 pr-3 text-neutral-400 text-xs font-mono">{c.category}</td>
                    <td className="py-3 pr-3 text-neutral-400 text-xs font-mono">{c.state || "—"}</td>
                    <td className="py-3 pr-3 text-right font-heading text-lg text-[#FFD300]">{c.clicks}</td>
                    <td className="py-3 pr-3 text-neutral-500 text-xs font-mono">{fmtDate(c.last_clicked)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="surface-card p-6">
        <div className="overline mb-4">// RECENT CLICKS (LAST 50)</div>
        {(d.recent || []).length === 0 ? <Empty msg="No recent clicks." /> : (
          <ul className="space-y-2">
            {d.recent.map((r) => (
              <li key={r.id} className="border-b border-white/5 pb-2 flex justify-between items-center text-sm">
                <div>
                  <div className="text-white">{r.title}</div>
                  <div className="text-neutral-500 text-xs font-mono">{r.user_email || "anonymous"} · {r.category}{r.state ? ` · ${r.state}` : ""}</div>
                </div>
                <div className="font-mono text-xs text-neutral-500">{fmtDate(r.clicked_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
